import React, { useEffect, useState } from 'react'
import { Linking } from 'react-native'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  InputField,
  Logo,
  Spinner,
  WelcomeMessage,
} from '../App/Components'
import {
  OptionsContainer,
  StyledScrollView,
} from '../App/Components/AppContainer'
import { setUserData } from '../App/Util'
import { useParams, useLocation } from '../Router'
import {
  grandUserAccess,
  checkIfCustomerIsExisting,
} from '../Settings/Store/Action'
import {
  needsCpRedirectForKnownCustomer,
  blocksUnknownCustomer,
} from '../App/Util/customerRecognitionGates'
import { customerPortalLinks } from '../App/Util/customerPortalUrl'
import { getCustomerSession } from '../cookies/session.cookies'
import { useAppDispatch, useAppSelector } from '../store/hooks'

import { CustomTitleMessage } from './Components/CustomTitleMessage/CustomTitleMessage'
import { ExtendedButtonWithText, FormContainer } from './style'

type FormValues = {
  email: string
}

export const ValidateEmail = () => {
  const { control, handleSubmit, errors } = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
  })
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { establishmentId } = useParams<{ [key: string]: string }>()
  const location = useLocation()
  const surveyPath = `${establishmentId}/surveys/`
  const surveyCheck = location.pathname.includes(surveyPath)

  const session = getCustomerSession(establishmentId)

  const { settings, isAccessGranted, isCustomerExisting } = useAppSelector(
    (state) => ({
      settings: state.settings.values,
      isAccessGranted: state.settings.isAccessGranted,
      isCustomerExisting: state.settings.isCustomerExisting,
    })
  )

  useEffect(() => {
    if (session?.email && !isAccessGranted) {
      setIsLoading(true)
      dispatch(
        checkIfCustomerIsExisting(establishmentId, session.email)
      ).then(() => setIsLoading(false))
    }
  }, [])

  const handleVerifiedEmail = (email: string, isExisting: boolean) => {
    if (!settings) return

    if (isExisting && needsCpRedirectForKnownCustomer(settings)) {
      const returnTo = window.location.href
      Linking.openURL(
        customerPortalLinks.login(establishmentId, returnTo, email)
      )
      return
    }

    if (!isExisting && !blocksUnknownCustomer(settings)) {
      dispatch(grandUserAccess())
    }
  }

  const onSubmit = (data: FormValues) => {
    setIsLoading(true)
    setUserData({ email: data.email })
    dispatch(checkIfCustomerIsExisting(establishmentId, data.email))
      .then((response) => {
        const responseData = response?.data
        const isExisting =
          typeof responseData === 'boolean'
            ? responseData
            : responseData?.is_existing === true
        handleVerifiedEmail(data.email, Boolean(isExisting))
      })
      .finally(() => setIsLoading(false))
  }

  if (surveyCheck) {
    dispatch(grandUserAccess())
    return null
  }

  if (session && isCustomerExisting) {
    dispatch(grandUserAccess())
    return null
  }

  if (typeof settings === 'undefined' || isLoading) {
    return <Spinner />
  }

  const customValidateEmailMessage = t(
    'We can not find your e-mail address in our client database, ' +
      'perhaps you misspelled it? We can not accept any new ' +
      'customers because we are at full capacity.'
  )

  return (
    <StyledScrollView>
      {isCustomerExisting === false &&
        settings.onlineAppointmentsExistingCustomersOnly && (
          <WelcomeMessage
            customMessage={customValidateEmailMessage}
            isValidateEmail
          />
        )}
      <Logo />
      <CustomTitleMessage
        isExistingCustomersOnly={settings.onlineAppointmentsExistingCustomersOnly}
        fontColor={settings.fontColor}
        customMessage={settings.onlineAppointmentsExistingCustomersOnlyMessage}
      />
      <FormContainer>
        <InputField
          inputLabel="Email"
          inputRef="email"
          fontColor={settings.fontColor}
          control={control}
          error={errors.email ? 'This field is required.' : ''}
          rules={{ required: true, pattern: /^\S+@\S+$/i }}
          fieldType="email"
          onSubmit={onSubmit}
        />
      </FormContainer>

      <ExtendedButtonWithText
        fontcolor={settings.highlightColor}
        backgroundcolor={settings.backgroundColor}
        onPress={handleSubmit(onSubmit)}
        hoversensitive
      >
        {t('Confirm')}
      </ExtendedButtonWithText>
      <OptionsContainer isHidePreviousAppointments />
    </StyledScrollView>
  )
}
